
import { useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import ConsumerHeader from "@/components/ConsumerHeader";
import WellnessCard from "@/components/WellnessCard";
import ProviderImagesFromFacilities from "@/components/ProviderImagesFromFacilities";
import { useLocationSearch } from "@/hooks/useLocationSearch";
import { Loader2, MapPin } from "lucide-react";

const Search = () => {
  const [searchParams] = useSearchParams();
  const [coordinates, setCoordinates] = useState<{latitude: number; longitude: number} | null>(null);
  
  const service = searchParams.get('service') || '';
  const location = searchParams.get('location') || '';
  const lat = searchParams.get('lat');
  const lng = searchParams.get('lng');

  console.log('Search page params:', { service, location, lat, lng });

  useEffect(() => {
    if (lat && lng) {
      const coords = {
        latitude: parseFloat(lat),
        longitude: parseFloat(lng),
      };
      console.log('Setting coordinates:', coords);
      setCoordinates(coords);
    }
  }, [lat, lng]);

  const { data: providers, isLoading, error } = useLocationSearch({
    latitude: coordinates?.latitude,
    longitude: coordinates?.longitude,
    maxDistance: 50,
    service: service,
    enabled: true,
  });

  console.log('Search hook results:', { providers, isLoading, error });

  // Helper: fallback to logo / provider image if no facility images
  const getProviderFallbackImages = (provider: any): string[] => {
    if (Array.isArray(provider.images) && provider.images.length > 0) {
      return provider.images;
    } else if (provider.image_url) {
      return [provider.image_url];
    }
    return [];
  };

  if (error) {
    console.error('Error loading search results:', error);
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ConsumerHeader />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Results Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Search Results</h1>
          <div className="flex flex-wrap gap-4 text-gray-600">
            {service && (
              <div className="flex items-center gap-2">
                <span className="font-medium">Service:</span>
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                  {service}
                </span>
              </div>
            )}
            {location && (
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                <span className="font-medium">Near:</span>
                <span>{location}</span>
              </div>
            )}
            {coordinates && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span>Coords: {coordinates.latitude.toFixed(4)}, {coordinates.longitude.toFixed(4)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Searching for providers...</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">Error loading search results</p>
            <p className="text-gray-500">Please try again or adjust your search criteria</p>
          </div>
        )}

        {/* Results */}
        {!isLoading && !error && providers && (
          <>
            <div className="mb-6">
              <p className="text-gray-600">
                Found {providers.length} provider{providers.length !== 1 ? 's' : ''}
                {coordinates && ' nearby'}
              </p>
            </div>

            {providers.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg mb-4">No providers found</p>
                <p className="text-gray-400">Try adjusting your search criteria or expanding your search area</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {providers.map((provider) => (
                  <ProviderImagesFromFacilities
                    key={provider.id}
                    providerId={provider.id}
                    fallbackImages={getProviderFallbackImages(provider)}
                  >
                    {(images) => (
                      <WellnessCard 
                        id={provider.id}
                        name={provider.name}
                        images={images}
                        rating={provider.rating}
                        reviewCount={provider.review_count}
                        location={provider.location}
                        services={provider.specialties}
                        price="£25"
                        slug={provider.slug}
                      />
                    )}
                  </ProviderImagesFromFacilities>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Search;
